export enum PaymentType {
  CreditCard = 1,
  PayPal = 2,
  GooglePay = 3,
  ApplePay = 4,
  ElectronicCheck = 99
}

export enum BankAccountType {
  Checking = 1,
  Savings = 2
}

export interface GooglePayConfig {
  isTestEnvironment: boolean;                           // 'true TEST or false 'PRODUCTION' refer to Google documentation
  googleMerchantId: string;                             // Requried when in Production; Merchant Id from Google
  testNonce: string;                                    // Braintree test nonce for testing
}

export interface ApplePayConfig {
  version: number;
  label: string;
  testNonce: string;
}
export interface OpenNotesPaymentInfoListItem {
  participantId: number;
  noteId: number;
  paymentDueDay: number;
  loanProgram: string;
  paymentGroup: string;
  loanCode: string;
  nextPaymentDueDate: string;
  noteDate: string;
  noteStatus: string;
  pastDueAmount: number;
  lateFeeDue: number;
  minimumAmountDue: number;
  regularPaymentAmount: number;
  totalAmountDue: number;
  payoffAmount: number;
  autoPayFlag: string;
  noteState: string;
  sortOrder: number;
}

export interface OpenNotesPaymentInfoListGroupFooter {
  isGroupFooter: boolean;
  paymentGroup: string;
  pastDueAmountTotal: number;
  lateFeeDueTotal: number;
  regularPaymentAmountTotal: number;
  totalAmountDueTotal: number;
  payoffAmountTotal: number;
  autoPayFlagTotal: string;
}

export interface PaymentAddress {
  participantId: number;
  noteIds: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
  acceptCreditCardFlag: boolean;
}

export interface PaymentConfirmationDialogData {
  dialogTitle: string;
  paymentOk: boolean;
  paymentError: string;
  paymentDetail: PaymentDetail;
}

export interface AutoPaymentConfirmationDialogData {
  dialogTitle: string;
  paymentOk: boolean;
  paymentError: string;
  autoPaymentDetail: AutoPaymentDetail;
}

export interface OpenNotesPaymentHistoryListItem {
  participantId: number;
  noteId: number;
  noteStatus: string;
  nextPaymentDueDate: string;
  pastDueAmount: number;
  lateFeeDue: number;
  totalAmountDue: number;
  payoffAmount: number;
  autoPayFlag: string;
  noteState: string;
  sortOrder: number;
  conversionnote: string;
}

export interface NotePaymentHistoryDetailsListItem {
  participantId: number;
  noteId: number;
  paymentDate: Date;
  paymentId: number;
  paymentAmount: number;
  principalAmountApplied: number;
  interestAmountApplied: number;
  feeAmount: number;
  paymentType: string;
}

export interface NoteBalanceAdjustmentsListItem {
  participantId: number;
  noteId: number;
  adjustmentId: number;
  adjustmentDate: Date;
  transactionAmount: number;
  principalAmount: number;
  interestAmount: number;
  feeAmount: number;
  adjustmentDescription: string;
}

export interface NoteTotalAmountPaidItem {
  participantId: number;
  noteId: number;
  paymentType: string;
  paymentAmount: number;
  principalAmountApplied: number;
  interestAmountApplied: number;
  feeAmount: number;
}

export interface AutoPayNotesListItem {
  participantId: number;
  noteId: number;
  noteStatus: string;
  nextPaymentDueDate: string;
  lastPaymentDate: Date;
  lastPaymentAmount: number;
  totalAmountDue: number;
  payoffAmount: number;
  autoPayStatus: string;
  noteState: string;
  loanProgram: string;
  sortOrder: number;
}

export interface NoteAutoPayInformation {
  participantId: number;
  noteId: number;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  acceptCreditCardFlag: boolean;
  firstPaymentDueDate: Date;
  nextPaymentDueDate: string;
  scheduleOneStartDate: Date;
  scheduleOneNumberPayments: number;
  scheduleOnePaymentAmount: number;
  scheduleTwoStartDate: Date;
  scheduleTwoNumberPayments: number;
  scheduleTwoPaymentAmount: number;
  scheduleThreeStartDate: Date;
  scheduleThreeNumberPayments: number;
  scheduleThreePaymentAmount: number;
  scheduleFourStartDate: Date;
  scheduleFourNumberPayments: number;
  scheduleFourPaymentAmount: number;
  scheduleFiveStartDate: Date;
  scheduleFiveNumberPayments: number;
  scheduleFivePaymentAmount: number;
}

export interface PaymentDetail {
  participantId: number;
  noteIds: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  paymentAmount: number;
  paymentDueDay: number;
  loanProgram: string;
  servicingCompany: string;
  paymentType: PaymentType;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountConfirmNumber: string;
  bankAccountType: BankAccountType;
  userName: string;
  paymentRef: string;
  checkPaymentId: string;
  confirmationCode: string;
  responseCode: string;
  responseMessage: string;

  /* Braintree tokenized payload */
  nonce: string;
  deviceData: string;
  partiFirstName: string;
  partiLastName: string;
  partiEmail: string;
  creditCardBin: string;
  creditCardType: string;
  lastFourCreditCard: string;
  creditCardExpMonth: string;
  creditCardExpYear: string;
  brainTreePaymentType: string;
  brainTreeLenderProgramCode: string;
}

export interface CreditCardPaymentInformation {
  participantId: number;
  companyCode: string;
  noteIds: string;
  paymentAmount: number;
  paymentDueDay: number;
  loanProgram: string;
  servicingCompany: string;
  fullName: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  userName: string;
  paymentRef: string;
  paymentType: number;

  /* From Braintree tokenized payload */
  nonce: string;
  partiFirstName: string;
  partiLastName: string;
  partiEmail: string;
  deviceData: string;
  creditCardBin: string;
  creditCardType: string;
  lastFourCreditCard: string;
  creditCardExpMonth: string;
  creditCardExpYear: string;
  brainTreePaymentType: string;
  brainTreeLenderProgramCode: string;
}

export interface ACHPaymentInformation {
  participantId: number;
  companyCode: string;
  noteIds: string;
  paymentAmount: number;
  paymentDueDay: number;
  bankAccountNumber: string;
  bankAccountType: string;
  bankRoutingNumber: string;
  fullName: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  userName: string;
  paymentRef: string;
  checkPaymentId: string;
}

export interface PayPalCreditCardPaymentReturn {
  transactionResponseResult: number;
  transactionResponseMessage: string;
  confirmationCode: string;

  // TEMP for side by side testing with Braintree
  emailProcessingResult: number;
}

export interface BraintreeCreditCardPaymentReturn {
  transactionResponseResult: number;
  emailProcessingResult: number;
  transactionResponseMessage: string;
  confirmationCode: string;
}

export interface AutoPaymentDetail {
  participantId: number;
  noteId: number;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  paymentAmount: number;
  paymentDueDay: number;
  loanProgram: string;
  servicingCompany: string;
  paymentType: PaymentType;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountConfirmNumber: string;
  bankAccountType: BankAccountType;
  userName: string;
  paymentRef: string;
  confirmationCode: string;
  responseCode: string;
  responseMessage: string;
  confirmationNumbers: string;
  firstPaymentAmount: number;
  firstPaymentDate: Date;
  isAutoPaymentSetup: boolean;
  isAutoPaymentCancel: boolean;

  /* Braintree tokenized payload */
  nonce: string;
  deviceData: string;
  partiFirstName: string;
  partiLastName: string;
  partiEmail: string;
  creditCardBin: string;
  creditCardType: string;
  lastFourCreditCard: string;
  creditCardExpMonth: string;
  creditCardExpYear: string;
  brainTreePaymentType: string;
  brainTreeLenderProgramCode: string;
}

export interface AutoPayCreditCardPaymentInformation {
  participantId: number;
  noteId: number;
  paymentAmount: number;
  paymentDueDay: number;
  loanProgram: string;
  servicingCompany: string;
  fullName: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  userName: string;
  paymentRef: string;
  confirmationCode: string;

  /* From Braintree tokenized payload */
  nonce: string;
  partiFirstName: string;
  partiLastName: string;
  partiEmail: string;
  deviceData: string;
  creditCardBin: string;
  creditCardType: string;
  lastFourCreditCard: string;
  creditCardExpMonth: string;
  creditCardExpYear: string;
  brainTreePaymentType: string;
  brainTreeLenderProgramCode: string;
}

export interface AutoPayACHPaymentInformation {
  noteId: number;
  fullName: string;
  bankAccountNumber: string;
  bankAccountType: string;
  bankRoutingNumber: string;
  userName: string;
  confirmationNumbers: string;
}

export interface CancelRecurringPaymentInformation {
  participantId: number;
  noteId: number;
  userName: string;
  loanProgram: string;
}

export interface BrainTreeClientToken {
  clientToken: string;
}

export interface BraintreeCustomerCreditCardInfo {
  customerId: string;
  creditCardType: number;
  creditCardTypeDesc: string;   // Description from tokenization or retrieved from vault (Transformed from number)
  lastFourCreditCard: string;
  creditCardExpDate: string;
  maskedCreditCardNumber: string;
}

// Credit card types defined after tokenization (Vaulted types are numeric)
export enum BraintreeCreditCardType {
  Visa = "Visa",
  MasterCard = "MasterCard",
  AmericanExpress = "American Express",
  Discover = "Discover",
}

export interface PendingPayment {
  pendingPaymentAmt: string;
}
